// Copyright (c) Zammo Inc.
// Client for the standalone "assistant" channel (ChatGPT-style /chat surface). Talks to the bot
// runtime via the /api/assistant dev proxy.
//
// AUTH MODEL: the URL access token is the durable credential (DB-backed, ~30-day expiry). From it we
// mint a short-lived *session* token that the bot keeps in an in-memory cache and that we carry as the
// Bearer on every call. That session is ephemeral by design — it vanishes whenever the bot process
// restarts / hot-reloads / recycles, or under cache pressure, long before its own TTL. So the client
// owns the session: it mints lazily and, on any 401, transparently re-mints from the still-valid
// access token and retries once. SSE carries the session token on the query string (EventSource can't
// set headers) and reconnects+re-mints the same way.

declare global {
  interface Window {
    /** Bot API origin for the dedicated static host (set by config.js). Empty/undefined → same-origin. */
    ZAMMO_CHAT_API_URL?: string;
  }
}

// API origin. Empty in local dev and the simulator (the dev-server proxy / setupProxy forwards
// /api/assistant to the bot, same-origin). On the dedicated static host (chat.zammo.ai) there's no
// proxy, so config.js sets window.ZAMMO_CHAT_API_URL to the bot's absolute origin and calls go there
// cross-origin (the bot allowlists the chat origin via CORS).
const API_ORIGIN = (typeof window !== 'undefined' && window.ZAMMO_CHAT_API_URL) || '';
const BASE = `${API_ORIGIN}/api/assistant`;

/**
 * Absolute URL to begin Microsoft (AAD) sign-in. This is a full-page browser redirect, so unlike the
 * XHR calls it MUST target the bot origin explicitly — a relative `/api/assistant/auth/start` would hit
 * the static host the chat is served from (whose 404-fallback returns index.html), looping sign-in.
 */
export function authStartUrl(token: string): string {
  return `${BASE}/auth/start?token=${encodeURIComponent(token)}`;
}

export interface AssistantConversation {
  id: string;
  title: string | null;
  lastWatermark: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssistantAttachment {
  name: string;
  contentType: string;
  url: string;
  isImage: boolean;
}

export interface AssistantActivity {
  watermark: number;
  role: 'user' | 'bot' | string;
  type: string | null;
  text: string | null;
  createdAt: string;
  attachments?: AssistantAttachment[] | null;
}

export interface AssistantSessionParams {
  /** The access token from the /chat URL (gates entry, resolves business + agent server-side). */
  token: string;
}

/** The signed-in user's display details (AAD sign-in only); absent for anonymous visitors. */
export interface AssistantUser {
  name?: string | null;
  email?: string | null;
}

/** Chat-surface customization set on the Channels page (all optional; falls back to defaults). */
export interface AssistantChatConfig {
  displayName?: string | null;
  iconUrl?: string | null;
  welcomeTitle?: string | null;
  welcomeSubtitle?: string | null;
  starters?: string[] | null;
}

/** Thrown when the URL access token itself is rejected (re-mint failed) — the link is invalid/expired. */
export class AssistantAccessRevokedError extends Error {
  constructor(message = 'This chat link is invalid or has expired.') {
    super(message);
    this.name = 'AssistantAccessRevokedError';
  }
}

/** Thrown when the channel requires sign-in (e.g. Microsoft) and the visitor isn't authenticated yet. */
export class AssistantAuthRequiredError extends Error {
  constructor(public readonly mode: string) {
    super(`Sign-in required (${mode}).`);
    this.name = 'AssistantAuthRequiredError';
  }
}

async function readError(res: Response): Promise<string> {
  const body = await res.text().catch(() => '');
  return `${res.status} ${res.statusText}${body ? ` — ${body}` : ''}`;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(await readError(res));
  // 204 No Content (DELETE / PUT rename) has an empty body — don't let res.json() throw on it.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// Mints a session from the URL access token, replaying the stored identity token (if any) so the
// server re-derives the same anonymous user id. Returns the session token plus a refreshed identity
// token to persist. The server derives identity ONLY from a valid signature, so we never send (or
// can forge) a raw user id.
async function mintSession(
  accessToken: string,
  identity: string | null
): Promise<{
  token: string;
  identity: string;
  agentName: string | null;
  config: AssistantChatConfig | null;
  user: AssistantUser | null;
}> {
  const res = await fetch(`${BASE}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: accessToken, identity }),
  });
  // A 401/404 here means the URL access token is no longer accepted — not recoverable by retrying.
  if (res.status === 401 || res.status === 404) throw new AssistantAccessRevokedError();
  const body = await json<{
    token?: string;
    identity?: string;
    agentName?: string | null;
    config?: AssistantChatConfig | null;
    authRequired?: string | null;
    user?: AssistantUser | null;
  }>(res);
  // The channel requires sign-in and the visitor isn't authenticated — no session was minted.
  if (body.authRequired) throw new AssistantAuthRequiredError(body.authRequired);
  if (!body.token || !body.identity) throw new Error('Session mint returned no token.');
  return {
    token: body.token,
    identity: body.identity,
    agentName: body.agentName ?? null,
    config: body.config ?? null,
    user: body.user ?? null,
  };
}

/**
 * Session-managing client for the assistant channel. Holds the durable URL access token and a
 * lazily-minted session token, and auto-recovers from a dropped session (bot restart / cache
 * eviction / failover) by re-minting and retrying — so an idle tab keeps working without a reload.
 */
export interface AssistantClient {
  /**
   * Mints the initial session (call once on bootstrap). Resolves with `{ authRequired }` set to the
   * sign-in mode (e.g. "aad") when the channel requires login and the visitor isn't authenticated —
   * the caller then shows a sign-in gate. Throws {@link AssistantAccessRevokedError} if the link is bad.
   */
  start(): Promise<{ authRequired: string | null }>;
  /** Persists an identity token obtained out-of-band (e.g. from the sign-in redirect fragment). */
  adoptIdentity(identityToken: string): void;
  /** Clears the stored identity + session (sign out). Reload afterwards to re-enter the auth gate. */
  signOut(): void;
  /** True once a session has been minted at least once (gates the composer UI). */
  isReady(): boolean;
  /** Display name of the pinned agent (who the visitor is talking to); null until minted or if the token pins no agent. */
  agentName(): string | null;
  /** Chat-surface customization (display name, icon, welcome text, starters); null until minted or if unset. */
  config(): AssistantChatConfig | null;
  /** The signed-in user's details (AAD only); null for anonymous visitors. */
  user(): AssistantUser | null;
  listConversations(): Promise<AssistantConversation[]>;
  createConversation(title?: string): Promise<AssistantConversation>;
  getActivities(
    conversationId: string,
    after?: number
  ): Promise<{ watermark: number; activities: AssistantActivity[] }>;
  streamMessage(
    conversationId: string,
    text: string,
    attachments?: AssistantAttachment[]
  ): Promise<{ watermark: number; text: string | null; inHandoff: boolean }>;
  uploadAttachments(conversationId: string, files: File[]): Promise<AssistantAttachment[]>;
  renameConversation(conversationId: string, title: string): Promise<void>;
  deleteConversation(conversationId: string): Promise<void>;
  /**
   * Opens an auto-recovering SSE tail for a conversation. Replays from `after` then tails live;
   * transparently re-mints the session and reconnects if the stream is dropped (incl. a 401 after a
   * bot restart). Returns a disposer that permanently closes the tail.
   */
  openStream(conversationId: string, after: number, handlers: StreamHandlers): () => void;
}

export interface StreamHandlers {
  onActivity?: (activity: AssistantActivity) => void;
  onToken?: (delta: string) => void;
  onReset?: () => void;
  onFinal?: (final: { watermark: number; text: string }) => void;
  onTyping?: () => void;
  // Transient ephemeral status ("Checking the logs…") shown on its own line, separate from the
  // answer bubble. Cleared by the consumer once real answer tokens arrive.
  onStatus?: (text: string) => void;
}

export function createAssistantClient(params: AssistantSessionParams): AssistantClient {
  let session: string | null = null;
  let minting: Promise<string> | null = null;
  let resolvedAgentName: string | null = null;
  let resolvedConfig: AssistantChatConfig | null = null;
  let resolvedUser: AssistantUser | null = null;

  // The signed identity token persists the anonymous user id (= conversation history) across reloads,
  // session expiry, and bot restarts. Keyed per access token so different agents/links don't collide.
  const identityKey = `assistant-identity:${params.token}`;
  const loadIdentity = (): string | null => {
    try {
      return window.localStorage.getItem(identityKey);
    } catch {
      return null;
    }
  };
  const saveIdentity = (id: string): void => {
    try {
      window.localStorage.setItem(identityKey, id);
    } catch {
      /* storage disabled (private mode) — identity just won't persist across reloads */
    }
  };

  // Single-flight session mint: concurrent callers share one in-flight request rather than
  // stampeding the mint endpoint when the session drops mid-burst.
  function mint(): Promise<string> {
    if (!minting) {
      minting = mintSession(params.token, loadIdentity())
        .then(({ token, identity, agentName, config, user }) => {
          if (identity) saveIdentity(identity);
          resolvedAgentName = agentName;
          resolvedConfig = config;
          resolvedUser = user;
          session = token;
          return token;
        })
        .finally(() => {
          minting = null;
        });
    }
    return minting;
  }

  async function ensureSession(): Promise<string> {
    return session ?? (await mint());
  }

  function authHeaders(token: string, withContentType = true): HeadersInit {
    return withContentType
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { Authorization: `Bearer ${token}` };
  }

  // Runs an authed request and, on a 401 (session gone server-side), re-mints once and retries.
  // `build` is given the current session token so it can set the Bearer (or omit Content-Type for
  // multipart uploads).
  async function authed<T>(build: (token: string) => Promise<Response>): Promise<T> {
    let token = await ensureSession();
    let res = await build(token);
    if (res.status === 401) {
      session = null; // the cached session is dead — force a fresh mint
      token = await mint(); // throws AssistantAccessRevokedError if the URL token is also dead
      res = await build(token);
    }
    return json<T>(res);
  }

  return {
    async start() {
      try {
        await ensureSession();
        return { authRequired: null };
      } catch (e) {
        if (e instanceof AssistantAuthRequiredError) return { authRequired: e.mode };
        throw e;
      }
    },
    adoptIdentity(identityToken: string) {
      saveIdentity(identityToken);
    },
    signOut() {
      try {
        window.localStorage.removeItem(identityKey);
      } catch {
        /* ignore */
      }
      session = null;
      resolvedUser = null;
      resolvedAgentName = null;
      resolvedConfig = null;
    },
    agentName() {
      return resolvedAgentName;
    },
    config() {
      return resolvedConfig;
    },
    user() {
      return resolvedUser;
    },
    isReady() {
      return session !== null;
    },
    listConversations() {
      return authed<AssistantConversation[]>((t) => fetch(`${BASE}/conversations`, { headers: authHeaders(t) }));
    },
    createConversation(title?: string) {
      return authed<AssistantConversation>((t) =>
        fetch(`${BASE}/conversations`, {
          method: 'POST',
          headers: authHeaders(t),
          body: JSON.stringify({ title: title ?? null }),
        })
      );
    },
    getActivities(conversationId: string, after = 0) {
      return authed<{ watermark: number; activities: AssistantActivity[] }>((t) =>
        fetch(`${BASE}/conversations/${conversationId}/activities?after=${after}`, { headers: authHeaders(t) })
      );
    },
    async streamMessage(conversationId, text, attachments) {
      const body = await authed<{ watermark?: number; text?: string | null; inHandoff?: boolean }>((t) =>
        fetch(`${BASE}/conversations/${conversationId}/messages/stream`, {
          method: 'POST',
          headers: authHeaders(t),
          body: JSON.stringify({ text, attachments: attachments ?? [] }),
        })
      );
      return { watermark: body?.watermark ?? 0, text: body?.text ?? null, inHandoff: body?.inHandoff ?? false };
    },
    uploadAttachments(conversationId, files) {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      return authed<AssistantAttachment[]>((t) =>
        fetch(`${BASE}/conversations/${conversationId}/attachments`, {
          method: 'POST',
          headers: authHeaders(t, false), // no Content-Type — let the browser set the multipart boundary
          body: form,
        })
      );
    },
    async renameConversation(conversationId, title) {
      await authed<unknown>((t) =>
        fetch(`${BASE}/conversations/${conversationId}`, {
          method: 'PUT',
          headers: authHeaders(t),
          body: JSON.stringify({ title }),
        })
      );
    },
    async deleteConversation(conversationId) {
      await authed<unknown>((t) =>
        fetch(`${BASE}/conversations/${conversationId}`, { method: 'DELETE', headers: authHeaders(t) })
      );
    },
    openStream(conversationId, after, handlers) {
      let es: EventSource | null = null;
      let closed = false;
      let cursor = after; // advances past replayed/streamed activities so a reconnect resumes, not replays
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      let backoffMs = 1000;

      const connect = async () => {
        if (closed) return;
        let token: string;
        try {
          token = await ensureSession();
        } catch {
          // URL access token is dead — stop trying; the next authed() call surfaces the fatal error.
          return;
        }
        if (closed) return;

        const url = `${BASE}/conversations/${conversationId}/stream?after=${cursor}&token=${encodeURIComponent(token)}`;
        const src = new EventSource(url);
        es = src;

        src.addEventListener('activity', (ev) => {
          backoffMs = 1000; // a delivered frame proves the stream is healthy — reset backoff
          const e = ev as MessageEvent;
          if (e.lastEventId) cursor = Number(e.lastEventId) || cursor;
          try {
            handlers.onActivity?.(JSON.parse(e.data) as AssistantActivity);
          } catch {
            /* ignore malformed frame */
          }
        });
        src.addEventListener('token', (ev) => {
          try {
            const { delta } = JSON.parse((ev as MessageEvent).data) as { delta?: string };
            if (delta) handlers.onToken?.(delta);
          } catch {
            /* ignore */
          }
        });
        src.addEventListener('reset', () => handlers.onReset?.());
        src.addEventListener('final', (ev) => {
          const e = ev as MessageEvent;
          if (e.lastEventId) cursor = Number(e.lastEventId) || cursor;
          try {
            const { watermark, text } = JSON.parse(e.data) as { watermark: number; text: string };
            handlers.onFinal?.({ watermark, text });
          } catch {
            /* ignore */
          }
        });
        src.addEventListener('typing', () => handlers.onTyping?.());
        src.addEventListener('status', (ev) => {
          try {
            const { text } = JSON.parse((ev as MessageEvent).data) as { text?: string };
            if (text) handlers.onStatus?.(text);
          } catch {
            /* ignore */
          }
        });

        src.onerror = () => {
          // A non-2xx (e.g. 401 after a bot restart) closes the EventSource permanently — native
          // auto-reconnect only kicks in for a dropped 200 stream, not an HTTP error. So when we see
          // CLOSED, tear down, drop the (likely dead) session, and reconnect ourselves with a fresh
          // mint. A transient blip leaves it CONNECTING and the browser handles it.
          if (src.readyState !== EventSource.CLOSED || closed) return;
          src.close();
          if (es === src) es = null;
          session = null; // force a re-mint on the reconnect — covers the restart/401 case
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, backoffMs);
          backoffMs = Math.min(backoffMs * 2, 15000);
        };
      };

      void connect();

      return () => {
        closed = true;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        es?.close();
        es = null;
      };
    },
  };
}
